import { useState } from 'react';

export default function CTA() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7f8l5aa509fjl5l07p0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="cta" className="bg-[#07091a] py-24 md:py-32 relative overflow-hidden">
      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-violet-600/6 blur-3xl pointer-events-none"></div>
      <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-indigo-500/5 blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
          {/* Left: Copy */}
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/20 rounded-full px-4 py-1.5 mb-8">
              <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse flex-shrink-0"></span>
              <span className="text-violet-300 text-xs font-medium tracking-widest uppercase whitespace-nowrap">Get Started</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              Ready to Eliminate<br />
              <span className="bg-gradient-to-r from-violet-400 via-indigo-300 to-blue-300 bg-clip-text text-transparent">the Gaps?</span>
            </h2>
            <p className="text-white/50 text-base md:text-lg leading-relaxed mb-10">
              Let's build the system your business actually needs. Book a strategy call and we'll map out exactly how to unify your operations and drive measurable growth.
            </p>

            {/* Differentiator callout */}
            <div className="border border-white/8 rounded-2xl bg-white/3 p-6 mb-8">
              <h3 className="text-white font-bold text-base mb-3">We Don't Add Tools. We Build Systems.</h3>
              <p className="text-white/40 text-sm leading-relaxed">
                Most providers layer software on top of problems. We eliminate the problem at the system level — creating a unified environment where everything works together.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { val: '38%', label: 'More booked jobs' },
                { val: '65%', label: 'Fewer missed leads' },
                { val: '40%', label: 'Less admin work' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-indigo-300 bg-clip-text text-transparent">{s.val}</p>
                  <p className="text-white/30 text-xs mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="flex-1 w-full">
            <div className="bg-white/4 border border-white/10 rounded-3xl p-8 md:p-10">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-violet-500/20 border border-violet-500/30 mx-auto mb-6">
                    <i className="ri-check-line text-violet-400 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">You're on the list!</h3>
                  <p className="text-white/50 text-sm">We'll be in touch within 24 hours to schedule your strategy call.</p>
                </div>
              ) : (
                <>
                  <h3 className="text-white font-bold text-xl mb-2">Book Your Strategy Call</h3>
                  <p className="text-white/40 text-sm mb-8">Free 30-minute session. No commitment required.</p>
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4"
                  >
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1 flex flex-col gap-1.5">
                        <label className="text-white/50 text-xs font-medium">First Name</label>
                        <input
                          name="first_name"
                          type="text"
                          required
                          placeholder="John"
                          className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-colors"
                        />
                      </div>
                      <div className="flex-1 flex flex-col gap-1.5">
                        <label className="text-white/50 text-xs font-medium">Last Name</label>
                        <input
                          name="last_name"
                          type="text"
                          required
                          placeholder="Smith"
                          className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-colors"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-white/50 text-xs font-medium">Email Address</label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="john@company.com"
                        className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-colors"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-white/50 text-xs font-medium">Business Type</label>
                      <select
                        name="business_type"
                        required
                        className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-violet-500/50 transition-colors cursor-pointer"
                      >
                        <option value="" className="bg-[#07091a]">Select your industry</option>
                        <option value="Home Services" className="bg-[#07091a]">Home Services</option>
                        <option value="Legal" className="bg-[#07091a]">Legal</option>
                        <option value="Healthcare & Medical" className="bg-[#07091a]">Healthcare &amp; Medical</option>
                        <option value="Real Estate" className="bg-[#07091a]">Real Estate</option>
                        <option value="Financial Services" className="bg-[#07091a]">Financial Services</option>
                        <option value="Other" className="bg-[#07091a]">Other</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-white/50 text-xs font-medium">What's your biggest challenge? <span className="text-white/20">(optional)</span></label>
                      <textarea
                        name="challenge"
                        placeholder="Briefly describe your current situation..."
                        maxLength={500}
                        rows={3}
                        className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-colors resize-none"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-violet-600 to-indigo-500 text-white font-bold py-4 rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap text-sm mt-2 disabled:opacity-60"
                    >
                      {loading ? 'Submitting...' : 'Book Your Strategy Call →'}
                    </button>
                    <p className="text-white/20 text-xs text-center">
                      By submitting, you agree to be contacted by Silo Intelligence.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
